/**
 * admin-google-analytics-api-serverless client.
 * GET /admin/ga/dashboard, GET /admin/ga/breakdown
 */

import { requestGaAdmin } from './auth';

const GA_DASHBOARD_PATH = '/admin/ga/dashboard';
const GA_BREAKDOWN_PATH = '/admin/ga/breakdown';

async function parseGaAdminResponse(res, path) {
  const text = await res.text();
  let body = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    // non-JSON
  }

  if (process.env.NODE_ENV !== 'production') {
    console.log('[ga-admin API]', path, res.status, body ?? text);
  }

  if (!res.ok) {
    const err = new Error(body?.msg || body?.message || `Request failed (${res.status})`);
    err.status = res.status;
    err.code = body?.code;
    err.data = body;
    throw err;
  }

  return body?.data ?? body;
}

function pickField(row, ...keys) {
  for (const key of keys) {
    if (row[key] != null) return row[key];
  }
  return undefined;
}

function toDateString(value) {
  if (!value) return '';
  if (typeof value === 'string') return value.slice(0, 10);
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  return String(value).slice(0, 10);
}

function toNumber(value, fallback = 0) {
  if (value == null || value === '') return fallback;
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

const DASHBOARD_FIELD_MAP = {
  date: ['Date', 'date', 'metric_date', 'MetricDate'],
  dau: ['DAU', 'dau'],
  wau: ['WAU', 'wau'],
  mau: ['MAU', 'mau'],
  active1DayUsers: ['Active1DayUsers', 'active_1_day_users'],
  active7DayUsers: ['Active7DayUsers', 'active_7_day_users'],
  active28DayUsers: ['Active28DayUsers', 'active_28_day_users'],
  totalUsers: ['TotalUsers', 'total_users'],
  newUsers: ['NewUsers', 'new_users'],
  sessions: ['Sessions', 'sessions'],
  engagedSessions: ['EngagedSessions', 'engaged_sessions'],
  eventCount: ['EventCount', 'event_count'],
  keyEvents: ['KeyEvents', 'key_events'],
  engagementSec: ['EngagementSec', 'engagement_sec', 'engagementSec'],
  averageSessionDuration: ['AverageSessionDuration', 'average_session_duration'],
  bounceRate: ['BounceRate', 'bounce_rate'],
  crashAffectedUsers: ['CrashAffectedUsers', 'crash_affected_users'],
  crashFreeUsersRate: ['CrashFreeUsersRate', 'crash_free_users_rate'],
  dauPerMau: ['DauPerMau', 'dau_per_mau'],
  dauPerWau: ['DauPerWau', 'dau_per_wau'],
  wauPerMau: ['WauPerMau', 'wau_per_mau'],
  eventCountPerUser: ['EventCountPerUser', 'event_count_per_user'],
  eventValue: ['EventValue', 'event_value'],
  eventsPerSession: ['EventsPerSession', 'events_per_session'],
  engagementRate: ['EngagementRate', 'engagement_rate'],
  screenPageViews: ['ScreenPageViews', 'screen_page_views'],
  screenPageViewsPerSession: ['ScreenPageViewsPerSession', 'screen_page_views_per_session'],
  screenPageViewsPerUser: ['ScreenPageViewsPerUser', 'screen_page_views_per_user'],
  scrolledUsers: ['ScrolledUsers', 'scrolled_users'],
  sessionsPerUser: ['SessionsPerUser', 'sessions_per_user'],
  userKeyEventRate: ['UserKeyEventRate', 'user_key_event_rate'],
  topEventName: ['TopEventName', 'top_event_name', 'topEventName'],
  topEventCount: ['TopEventCount', 'top_event_count', 'topEventCount'],
};

const BREAKDOWN_FIELD_MAP = {
  activeUsers: ['active_users', 'activeUsers', 'ActiveUsers'],
  active1DayUsers: ['active_1_day_users', 'active1DayUsers'],
  active7DayUsers: ['active_7_day_users', 'active7DayUsers'],
  active28DayUsers: ['active_28_day_users', 'active28DayUsers'],
  totalUsers: ['total_users', 'totalUsers'],
  newUsers: ['new_users', 'newUsers'],
  sessions: ['sessions', 'Sessions'],
  engagedSessions: ['engaged_sessions', 'engagedSessions'],
  eventCount: ['event_count', 'eventCount'],
  keyEvents: ['key_events', 'keyEvents'],
  engagementSec: ['engagement_sec', 'engagementSec'],
  averageSessionDuration: ['average_session_duration', 'averageSessionDuration'],
  bounceRate: ['bounce_rate', 'bounceRate'],
  crashAffectedUsers: ['crash_affected_users', 'crashAffectedUsers'],
  crashFreeUsersRate: ['crash_free_users_rate', 'crashFreeUsersRate'],
  dauPerMau: ['dau_per_mau', 'dauPerMau'],
  dauPerWau: ['dau_per_wau', 'dauPerWau'],
  wauPerMau: ['wau_per_mau', 'wauPerMau'],
  eventCountPerUser: ['event_count_per_user', 'eventCountPerUser'],
  eventValue: ['event_value', 'eventValue'],
  eventsPerSession: ['events_per_session', 'eventsPerSession'],
  engagementRate: ['engagement_rate', 'engagementRate'],
  screenPageViews: ['screen_page_views', 'screenPageViews'],
  screenPageViewsPerSession: ['screen_page_views_per_session', 'screenPageViewsPerSession'],
  screenPageViewsPerUser: ['screen_page_views_per_user', 'screenPageViewsPerUser'],
  scrolledUsers: ['scrolled_users', 'scrolledUsers'],
  sessionsPerUser: ['sessions_per_user', 'sessionsPerUser'],
  userKeyEventRate: ['user_key_event_rate', 'userKeyEventRate'],
};

function normalizeFields(row, fieldMap, stringKeys = []) {
  const out = {};
  Object.entries(fieldMap).forEach(([key, aliases]) => {
    const raw = pickField(row, ...aliases);
    if (key === 'date') {
      out.date = toDateString(raw);
    } else {
      out[key] = stringKeys.includes(key) ? (raw || '') : toNumber(raw);
    }
  });
  return out;
}

/** Normalize dashboard row (PascalCase or snake_case) to a stable shape. */
export function normalizeDashboardRow(row) {
  if (!row || typeof row !== 'object') return null;
  const normalized = normalizeFields(row, DASHBOARD_FIELD_MAP, ['topEventName']);
  if (!normalized.date) return null;
  return normalized;
}

/** Normalize breakdown row to a stable shape. */
export function normalizeBreakdownRow(row) {
  if (!row || typeof row !== 'object') return null;
  const normalized = normalizeFields(row, BREAKDOWN_FIELD_MAP);
  const dimension = pickField(row, 'dimension', 'Dimension');
  if (dimension == null || String(dimension).trim() === '') return null;
  normalized.dimension = String(dimension);
  if (normalized.date) {
    normalized.date = toDateString(normalized.date);
    normalized.dimension = normalized.date;
  }
  return normalized;
}

/** GET /admin/ga/dashboard?from=YYYY-MM-DD&to=YYYY-MM-DD */
export async function getGaDashboard(from, to, accessToken) {
  const params = new URLSearchParams({ from, to });
  const path = `${GA_DASHBOARD_PATH}?${params.toString()}`;
  const res = await requestGaAdmin(path, { method: 'GET' }, accessToken);
  const data = await parseGaAdminResponse(res, path);
  const rows = Array.isArray(data) ? data : [];
  return rows.map(normalizeDashboardRow).filter(Boolean);
}

/** GET /admin/ga/breakdown?dimension=...&from=&to=&platform=&appVersion=&eventName= */
export async function getGaBreakdown(dimension, from, to, accessToken, filters = {}) {
  const params = new URLSearchParams({ dimension, from, to });
  if (filters.platform) params.set('platform', filters.platform);
  if (filters.appVersion) params.set('appVersion', filters.appVersion);
  if (filters.eventName) params.set('eventName', filters.eventName);
  const path = `${GA_BREAKDOWN_PATH}?${params.toString()}`;
  const res = await requestGaAdmin(path, { method: 'GET' }, accessToken);
  const data = await parseGaAdminResponse(res, path);
  const rows = Array.isArray(data) ? data : [];
  return rows.map(normalizeBreakdownRow).filter(Boolean);
}

/** Existing GA read endpoint, preserving absent metrics for product interpretation. */
export async function getGaProductBreakdown(dimension, from, to, accessToken, filters = {}) {
  const params = new URLSearchParams({ dimension, from, to });
  if (filters.platform) params.set('platform', filters.platform);
  if (filters.appVersion) params.set('appVersion', filters.appVersion);
  if (filters.eventName) params.set('eventName', filters.eventName);
  const path = `${GA_BREAKDOWN_PATH}?${params.toString()}`;
  const res = await requestGaAdmin(path, { method: 'GET' }, accessToken);
  const data = await parseGaAdminResponse(res, path);
  if (!Array.isArray(data)) throw new Error('GA breakdown response must be an array');
  const readMetric = (row, keys) => {
    const value = pickField(row, ...keys);
    if (!['number', 'string'].includes(typeof value) || (typeof value === 'string' && !value.trim())) return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
  };
  return data.flatMap(row => {
    if (!row || typeof row !== 'object') return [];
    const name = pickField(row, 'dimension', 'Dimension');
    if (name == null || !String(name).trim()) return [];
    return [{ dimension: String(name), eventCount: readMetric(row, [...BREAKDOWN_FIELD_MAP.eventCount, 'EventCount']), activeUsers: readMetric(row, BREAKDOWN_FIELD_MAP.activeUsers) }];
  });
}

/** Read-only GA report for Plaza visit summaries. No Admin-side aggregation service. */
export async function getGaPlazaVisits(from, to, accessToken, filters = {}, plazaId = '') {
  const params = new URLSearchParams({ dimension: 'plazaVisit', from, to });
  if (filters.platform) params.set('platform', filters.platform);
  if (filters.appVersion) params.set('appVersion', filters.appVersion);
  if (plazaId) params.set('plazaId', plazaId);
  const path = `${GA_BREAKDOWN_PATH}?${params.toString()}`;
  const res = await requestGaAdmin(path, { method: 'GET' }, accessToken);
  const data = await parseGaAdminResponse(res, path);
  if (!Array.isArray(data)) throw new Error('Plaza GA response must be an array');
  return data;
}
